import React, { useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Wallet, ShieldOff, Building, Users, Lock, Shield } from "lucide-react";
import Modal, { Ext } from "./Modal";

const items = [
  {
    icon: BadgeCheck,
    title: "Titulares del RETEF",
    text: "Registro concedido por la AEAT bajo el Real Decreto 249/2023. Operamos con todas las garantías.",
    footnote: "Nº Registro REDEF: 004VBSG6OC001684166 · Verificable en sede electrónica AEAT",
    modal: (
      <>
        <p>Somos titulares del RETEF <strong>Nº 004VBSG6OC001684166</strong>, concedido por la AEAT bajo el Real Decreto 249/2023. Actuamos con plena cobertura legal. El cliente no necesita obtener su propio registro ni cumplir los requisitos administrativos.</p>
        <p><Ext href="https://www.agenciatributaria.es/AEAT.internet/Inicio/La_Agencia_Tributaria/Aduanas_e_Impuestos_Especiales/Impuestos_Especiales/Registro_territorial.shtml">Verificable en sede AEAT</Ext></p>
      </>
    ),
  },
  {
    icon: Wallet,
    title: "Liquidez inmediata",
    text: "El impuesto queda suspendido hasta la puesta a consumo. Liberas capital atrapado en IIEE.",
    modal: (
      <>
        <h4>Ejemplo numérico real</h4>
        <p>Importación de 50.000 litros de whisky a 40% vol.:</p>
        <ul>
          <li>Litros de alcohol puro: 20.000 l.a.p.</li>
          <li>Impuesto especial: 20.000 × 10,18 = <strong>203.600€</strong></li>
        </ul>
        <p>En régimen suspensivo, esos 203.600€ permanecen en tu tesorería hasta la venta.</p>
      </>
    ),
  },
  {
    icon: ShieldOff,
    title: "Sin RETEF propio",
    text: "No necesitas obtener tu propio registro ni constituir garantías ante la AEAT.",
    modal: (
      <>
        <p>Obtener el RETEF propio requiere: solicitud formal, acreditación de capacidad técnica y económica, constitución de garantía ante la AEAT y cumplimiento de obligaciones contables y declarativas continuas.</p>
        <p><strong>Con FiscalBond, ninguno de estos requisitos recae sobre tu empresa.</strong></p>
      </>
    ),
  },
  {
    icon: Building,
    title: "Solución integral",
    text: "Colaboramos con depósitos fiscales autorizados para cubrir también el almacenamiento físico.",
    modal: (
      <>
        <p>FiscalBond cubre en una sola relación comercial:</p>
        <ul>
          <li>La parte fiscal (RETEF, declaraciones, AEAT)</li>
          <li>La parte documental (e-AD, DAA, Libro de IIEE)</li>
          <li>La parte logística (INCOTERMS, transitarios, depósitos)</li>
          <li>La parte financiera (diferimiento del impuesto, optimización de garantías)</li>
        </ul>
      </>
    ),
  },
  {
    icon: Users,
    title: "Equipo especializado",
    text: "Profesionales con años de experiencia en IIEE, CAE, garantías y régimen suspensivo.",
    modal: (
      <>
        <p>No asesoramos sobre algo que desconocemos. Hemos importado, almacenado, reexpedido y vendido productos en régimen suspensivo como empresa antes de ofrecer estos servicios a terceros.</p>
        <h4>Conocimiento certificado</h4>
        <p>Ley 38/1992, RD 1165/1995, EMCS, INCOTERMS 2020, Reglamento 952/2013.</p>
      </>
    ),
  },
  {
    icon: Lock,
    title: "Discreción total",
    text: "Confidencialidad y cumplimiento normativo absoluto en cada operación.",
    modal: (
      <>
        <h4>Información que protegemos</h4>
        <p>Volúmenes de importación, precios de adquisición, proveedores, destinos de distribución y márgenes comerciales.</p>
        <p>Ofrecemos NDA a todos los clientes. Cumplimiento RGPD y LOPDGDD garantizado.</p>
      </>
    ),
  },
  {
    icon: Shield,
    title: "Seguridad jurídica",
    text: "Supervisión técnica especializada y trazabilidad documental completa en cada operación.",
    modal: (
      <>
        <p>Operar en impuestos especiales sin especialista puede generar:</p>
        <ul>
          <li>Liquidación incorrecta → recargos e intereses de demora</li>
          <li>Documentación incompleta → incautación del producto</li>
          <li>Incumplimiento de plazos EMCS → penalizaciones administrativas</li>
          <li>Garantías insuficientes → intervención de la Oficina Gestora</li>
        </ul>
        <p>Con FiscalBond, todas las operaciones tienen supervisión técnica especializada y trazabilidad documental completa.</p>
        <p><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1992-26834">Régimen sancionador: Ley 38/1992, Título III</Ext></p>
      </>
    ),
  },
];

export const Advantages = () => {
  const [open, setOpen] = useState(null);
  return (
    <section
      id="ventajas"
      data-testid="advantages-section"
      className="relative bg-[#0A1628] py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute top-20 left-1/3 w-[500px] h-[500px] rounded-full bg-[#FF6B00]/8 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#FF6B00] font-semibold">
            ¿Por qué elegirnos?
          </span>
          <h2
            className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight"
            style={{ fontFamily: "Poppins" }}
          >
            Siete razones para confiar tu operativa fiscal a FiscalBond.
          </h2>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(({ icon: Icon, title, text, footnote }, i) => (
            <motion.button
              type="button"
              key={title}
              onClick={() => setOpen(i)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              data-testid={`advantage-card-${i}`}
              className="group relative text-left bg-gradient-to-br from-[#1E3A5F]/40 to-transparent backdrop-blur-sm border border-white/10 rounded-2xl p-7 hover:border-[#0066FF] hover:-translate-y-1 cursor-pointer transition-all overflow-hidden"
            >
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B00]/15 border border-[#FF6B00]/30 flex items-center justify-center flex-shrink-0 group-hover:bg-[#FF6B00] transition-colors">
                  <Icon className="w-6 h-6 text-[#FF6B00] group-hover:text-white transition-colors" strokeWidth={1.7} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Poppins" }}>
                    {title}
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{text}</p>
                  {footnote && (
                    <p data-testid={`advantage-footnote-${i}`} className="mt-3 pt-3 border-t border-white/10 text-xs text-[#FF6B00] font-mono tracking-wide leading-relaxed">
                      {footnote}
                    </p>
                  )}
                  <span className="mt-3 inline-block text-xs font-semibold text-[#3B8AFF]">Saber más →</span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {open !== null && (
        <Modal open={open !== null} onClose={() => setOpen(null)} title={items[open].title} eyebrow="Ventaja">
          {items[open].modal}
        </Modal>
      )}
    </section>
  );
};

export default Advantages;
